/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Announced } from '@fluentui/react/lib/Announced';
import { ActionButton, IButtonProps, IButtonStyles } from '@fluentui/react/lib/Button';
import { IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type MainFiltersHeaderProps = {
    /**
     * The header title
     */
    title: string;

    /**
     * Count of the filtered items
     */
    resultsCount: number;

    /**
     * Determines whether at least one filter is applied or not.
     */
    areFiltersApplied: boolean;

    /**
     * Handler function for clear all filters button
     */
    onClearAllClick: () => void;
};

const messages = defineMessages({
    clearAllFilters: {
        id: 'core.mainFiltersHeader.clearAllFilters',
        defaultMessage: 'Reset filter'
    },
    resultsCount: {
        id: 'core.mainFiltersHeader.resultsCount',
        defaultMessage: 'results'
    },
    resetAnnouncedMessage: {
        id: 'core.mainFiltersHeader.resetAnnouncedMessage',
        defaultMessage: 'Filters are reset'
    }
});

const clearBtnStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { paddingLeft: 0 } });
const clearIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { marginLeft: 0 } });

/**
 * Represents the main filters blade header
 */
export const MainFiltersHeader = (props: MainFiltersHeaderProps) => {
    const { resultsCount, onClearAllClick, areFiltersApplied } = props;
    const { localize } = useLocalization();

    return (
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <ActionButton
                styles={clearBtnStyles}
                iconProps={{ iconName: 'ClearFilter', styles: clearIconStyles }}
                text={localize(messages.clearAllFilters)}
                onClick={onClearAllClick}
                disabled={!areFiltersApplied}
            />
            {!areFiltersApplied && <Announced message={localize(messages.resetAnnouncedMessage)} />}
            <Stack>{`${resultsCount} ${localize(messages.resultsCount)}`}</Stack>
        </Stack>
    );
};
